/**
 * Shopify / Dawn compatibility shim.
 *
 * Goal: maximize compatibility with Shopify apps and Dawn-conventions
 * without changing the theme's behavior or visual style.
 *
 * Exposes:
 *  1. window.PUB_SUB_EVENTS — Dawn 2.0+ event name constants.
 *  2. window.subscribe(eventName, cb)  → returns unsubscribe fn.
 *  3. window.publish(eventName, data)
 *  4. window.Shopify.formatMoney(amount, format?) — euro fallback formatter.
 *
 * Bridge: dispatches DOM CustomEvents on document for every publish() call so
 * legacy listeners that hooked into document.addEventListener also fire, and
 * subscribes to our internal `variant:change`, `cart:add`, `cart:update`
 * events so the publish() bus is fed automatically.
 */
(function () {
  if (window.PUB_SUB_EVENTS) return; // already loaded

  // 1. Constants — names mirror Dawn 2.0+ (PUB_SUB_EVENTS in pubsub.js).
  window.PUB_SUB_EVENTS = Object.freeze({
    cartUpdate: 'cart-update',
    cartError: 'cart-error',
    quantityUpdate: 'quantity-update',
    variantChange: 'variant-change',
    cartDrawerOpen: 'cart-drawer-open',
    cartDrawerClose: 'cart-drawer-close',
  });

  // 2 + 3. publish/subscribe bus.
  const subscribers = {};

  function subscribe(eventName, callback) {
    if (subscribers[eventName] === undefined) subscribers[eventName] = [];
    subscribers[eventName] = [...subscribers[eventName], callback];
    return function unsubscribe() {
      subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
    };
  }

  function publish(eventName, data) {
    if (subscribers[eventName]) {
      subscribers[eventName].forEach((cb) => {
        try {
          cb(data);
        } catch (err) {
          console.error(`[PubSub] subscriber error for "${eventName}"`, err);
        }
      });
    }
    document.dispatchEvent(new CustomEvent(eventName, { detail: data }));
  }

  window.subscribe = subscribe;
  window.publish = publish;

  // 4. Shopify.formatMoney shim. Apps occasionally call it directly.
  window.Shopify = window.Shopify || {};
  if (typeof window.Shopify.formatMoney !== 'function') {
    window.Shopify.formatMoney = function (amount /* , format */) {
      // Input contract is the same as our internal formatMoney:
      //   - number → cents (e.g. 199  → "€1,99")
      //   - string → euros decimal (e.g. "1.99" → "€1,99")
      if (amount == null || amount === '') return '';
      if (typeof amount === 'string') {
        const asFloat = parseFloat(amount);
        if (isNaN(asFloat)) return '';
        return '€' + asFloat.toFixed(2).replace('.', ',');
      }
      const numCents = Number(amount);
      if (isNaN(numCents)) return '';
      return '€' + (numCents / 100).toFixed(2).replace('.', ',');
    };
  }

  // Bridge: our internal DOM events (variant:change, cart:add, cart:update)
  // → republish on the Dawn PUB_SUB_EVENTS channel so subscribe(...) works.
  function bridge(domEvent, pubsubEvent) {
    document.addEventListener(domEvent, (e) => {
      // Republish but do NOT re-dispatch the CustomEvent (publish already does),
      // and skip if we're the one who fired it (no-op since names differ).
      if (subscribers[pubsubEvent]) {
        subscribers[pubsubEvent].forEach((cb) => {
          try {
            cb(e.detail);
          } catch (err) {
            console.error(`[PubSub bridge] subscriber error for "${pubsubEvent}"`, err);
          }
        });
      }
    });
  }

  bridge('variant:change', window.PUB_SUB_EVENTS.variantChange);
  bridge('cart:add', window.PUB_SUB_EVENTS.cartUpdate);
  bridge('cart:update', window.PUB_SUB_EVENTS.cartUpdate);
})();

/* ------------------------- <variant-selects> shim ------------------------- */
/**
 * Minimal `<variant-selects>` custom element so apps that target Dawn's
 * variant picker (color swatches, custom selectors, Subscribe & Save UIs)
 * can find an element by name. The real variant-selection logic stays in
 * `<product-info>`; this element only exposes:
 *   - element.options() — Array of {position, name, values}
 *   - element.currentVariant — best-effort accessor (reads window product json)
 * and dispatches `change` events that bubble out.
 */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Allow apps that listen on this element for 'change' to get notified
    // whenever <product-info> applies a new variant.
    this._onVariantChange = (event) => {
      if (!event.detail || !event.detail.sectionId) return;
      if (event.detail.sectionId !== this.dataset.section) return;
      this.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          detail: { variant: event.detail.variant, available: event.detail.available },
        })
      );
    };
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    if (this._onVariantChange) document.removeEventListener('variant:change', this._onVariantChange);
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}

/* --------------------------- <media-gallery> shim ------------------------- */
/**
 * Minimal `<media-gallery>` wrapper. Apps for image-zoom / lightbox / UGC
 * gallery target this element name (Dawn convention). Our internal media
 * gallery logic stays in the carousel script.
 */
class MediaGallery extends HTMLElement {}
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
